import template from './employee_document_count_report.html';
import * as Utils from '../../lib/savanna_utils.js';
import tr from '../../lib/default-translator.js';

class ReportEmployeeDocumentCountReportViewModel
{
	constructor (page)
	{
		this.page = page;
		this.loader = ko.observable(false);

		this.employee_document_count_report = ko.observableArray('');
		this.employee_document_types = ko.observableArray('');
		this.current_department_id = ko.observable();
		this.selected_department = ko.observable();
		this.show_hint = ko.observable(true);
		this.columns = ko.observableArray(['Firstname', 'Surname', 'ID number', 'Department']);
		this.visible_columns = ko.observableArray(['Firstname', 'Surname', 'ID number', 'Department']);
		this.show_no_data_message = ko.observable(false);

		this.default_columns = ['Firstname', 'Surname', 'ID number', 'Department'];

		// Load saved settings from localStorage
		this.load_saved_settings();

		// Subscribe to changes and save them
		this.visible_columns.subscribe((newColumns) => {
			this.save_settings();
		});

		// Update columns when employee document types are loaded
		this.employee_document_types.subscribe((new_types) => {
			let type_columns = new_types.map(type => type.type);
			let all_columns = ['Firstname', 'Surname', 'ID number', 'Department', ...type_columns];
			this.columns(all_columns);

			// If no saved columns or only default columns, select all columns by default
			if (this.visible_columns().length === this.default_columns.length) {
				// Select all columns on first load
				this.visible_columns([...all_columns]);
			} else {
				// Validate visible columns against available columns
				let valid_columns = this.visible_columns().filter(col =>
					this.default_columns.includes(col) || type_columns.includes(col)
				);

				// Ensure at least default columns are visible
				if (valid_columns.length === 0)
					valid_columns = [...this.default_columns];
				
				this.visible_columns(valid_columns);
			}
			
			this.save_settings();
		});

		this.selected_department.subscribe((newValue) => {
			this.employee_document_count_report([]);
			this.show_hint(true);

			if (newValue != null)
				this.current_department_id(newValue.department_id);
			else
				this.current_department_id(null);
		});
	}

	load_saved_settings()
	{
		try
		{
			let saved_settings = JSON.parse(localStorage.getItem('employee_document_count_report_settings')) || {};
			if (saved_settings.visible_columns)
				this.visible_columns(saved_settings.visible_columns);
		}
		catch (e)
		{
			console.warn('Failed to load saved settings:', e);
		}
	}

	save_settings()
	{
		try
		{
			let settings = {
				visible_columns: this.visible_columns(),
			};
			localStorage.setItem('employee_document_count_report_settings', JSON.stringify(settings));
		}
		catch (e)
		{
			console.warn('Failed to save settings:', e);
		}
	}
}

class ReportEmployeeDocumentCount
{
	constructor (bindings)
	{
		this.bindings = bindings;
		this.viewModel = new ReportEmployeeDocumentCountReportViewModel(this);
	}

	async init()
	{
		// check access to page
		if (await Utils.redirectOnAccessDenied('EmployeeDocumentCountReport'))
			return;

		document.title = tr('Employee Document Count Report');
		window.current_page_title(tr('Employee Document Count Report'));

		let res = await Grape.fetches.getJSON('/api/record', {
			schema: 'public',
			table : 'employee_document_types',
			limit: 1000000,
			sortfield: 'type',
			sortorder: 'ASC'
		});

		if (res)
			this.viewModel.employee_document_types(res.records);
	}

	updateData()
	{
		if (this.viewModel.current_department_id() != null)
			this.update_report_data();
	}

	async update_report_data()
	{
		this.viewModel.loader(true);
		this.viewModel.employee_document_count_report([]);

		if (this.viewModel.current_department_id() != '' && this.viewModel.current_department_id() != null && this.viewModel.current_department_id() != undefined)
		{
			let res = await Grape.fetches.getJSON('/api/record', {
				schema: 'savanna',
				table: 'v_employee_document_count',
				limit: 1000000,
				filter: [{field: 'department_id', operator: '=', value: this.viewModel.current_department_id()}],
			});

			if (res.total === 0)
				this.viewModel.show_no_data_message(true);

			if (res)
			{
				this.viewModel.employee_document_count_report(res.records);
				this.viewModel.loader(false);
				this.viewModel.show_hint(false);
			}
		}
		else
		{
			let res = await Grape.fetches.getJSON('/api/record', {
				schema: 'savanna',
				table: 'v_employee_document_count',
				limit: 1000000
			});

			if (res)
			{
				this.viewModel.employee_document_count_report(res.records);
				this.viewModel.loader(false);
				this.viewModel.show_hint(false);
			}
		}
	}

	show_report_help()
	{
		Grape.dialog.open('EmployeeDocumentCountReportHelp', { 'employee_id': this.bindings.id }, {});
	}

	async downLoadClicked(v, e)
	{
		// Get only visible columns for CSV export
		let visible_cols = this.viewModel.visible_columns();
		let table = document.getElementById('employee_document_count_report');
		
		if (!table) return;

		let csv_content = [];

		// Create header row with only visible columns
		let header_row = visible_cols.map(col => `"${col}"`);
		csv_content.push(header_row.join(','));

		// Get column indices for visible columns
		let all_columns = this.viewModel.columns();
		let visible_indices = visible_cols.map(col => all_columns.indexOf(col));

		// Process each employee record
		this.viewModel.employee_document_count_report().forEach(employee => {
			let row = [];

			visible_cols.forEach(col => {
				if (col === 'Firstname') {
					row.push(`"${employee.firstname || ''}"`);
				} else if (col === 'Surname') {
					row.push(`"${employee.surname || ''}"`);
				} else if (col === 'ID number') {
					row.push(`"${employee.id_number || ''}"`);
				} else if (col === 'Department') {
					row.push(`"${employee.department || ''}"`);
				} else {
					// This is a document type column
					let doc_count = employee.get_employee_documents_count?.find(
        				doc => doc.document_type === col
					);
					row.push(`"${doc_count ? doc_count.count : '0'}"`);
				}
			});

			csv_content.push(row.join(','));
		});

		let csv_string = csv_content.join('\n');
		
		if (csv_string === null || csv_string === undefined || csv_string === '')
			return;

		let blob = new Blob([csv_string], { type: 'text/csv;charset=utf-8;' });
		let link = document.createElement('a');

		link.href = URL.createObjectURL(blob);
		link.setAttribute('download', 'employee_document_count_report.csv');

		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	}
}

export default {
	route: '[/]report/employee_document_count_report',
	page_class: ReportEmployeeDocumentCount,
	template: template,
	template_filename: 'translation/employee_document_count_report'
};